import { v4 as uuid } from 'uuid';
import { cloneDeep } from 'lodash-es';
import { ActivityInput, TreatmentPlanInput } from './treatmentPlanTypes';
import { ActivityDefinition } from '../../../../types/library';
import {
  CompareFn,
  comparisonRoutine,
  isEmpty,
} from '../../../../state-manager/utils/compare';
import { RadioInputFieldOption } from '../../../commons/form/forStateFrom';
import { MONITORING_TAGS, monitoringTagFor } from '../../../commons/monitoringTag';
import { tooltips } from '../commons/tooltips';
import { Activity } from '../../../../models/Activity';
import { TreatmentPlan } from '../../../../models/TreatmentPlan';
import { CSSObject } from '@emotion/react';
import { FormErrors } from '../../../commons/form/useForm';
import { useSelector } from 'react-redux';
import { getPatientActivitiesExecutionsSelector } from '../../../../models/ActivityExecution';
import { TreatmentPlanTemplate } from '../../../../models/TreatmentPlanTemplate';
import { toDatePickerFormat } from '../../../../utils/dateUtils';
import { createDefaultName } from './defaultPlanName';

function compareSide(
  nextCompareFn: CompareFn<ActivityInput> | undefined,
  a: ActivityInput,
  b: ActivityInput,
): number {
  const valA = a.side ?? '';
  const valB = b.side ?? '';
  return comparisonRoutine(a, b, valB, valA, nextCompareFn);
}

function comparePosition(
  nextCompareFn: CompareFn<ActivityInput> | undefined,
  activityDefinitions: Record<string, ActivityDefinition>,
  a: ActivityInput,
  b: ActivityInput,
): number {
  const getWight = (position?: string) => {
    switch (position) {
      case 'Standing':
        return 0;
      case 'Seated':
        return 1;
      case 'Supine':
        return 2;
      case 'Side-Lying':
        return 3;
      default:
        return 4;
    }
  };
  const valA = getWight(activityDefinitions[a.activityDefinitionId]?.position);
  const valB = getWight(activityDefinitions[b.activityDefinitionId]?.position);
  return comparisonRoutine(a, b, valA, valB, nextCompareFn);
}

export function orderActivitiesByPosition(
  activities: ActivityInput[],
  activityDefinitions: Record<string, ActivityDefinition>,
  order?: 'desc' | 'asc',
): ActivityInput[] {
  const secondCompare = compareSide.bind(undefined, undefined);
  const mainCompare = comparePosition.bind(undefined, secondCompare, activityDefinitions);

  // Compare function to sort positions alphabetically
  const compareByPositionAZ = (a: ActivityInput, b: ActivityInput): number => {
    const positionA = activityDefinitions[a.activityDefinitionId]?.position || '';
    const positionB = activityDefinitions[b.activityDefinitionId]?.position || '';

    return positionA.localeCompare(positionB);
  };

  const sortedActivities = [...activities].sort(mainCompare).sort(compareByPositionAZ);
  return order === 'desc' ? sortedActivities.reverse() : sortedActivities;
}

export function toggleSides(activities: ActivityInput[]): ActivityInput[] {
  return activities.map((activity) => {
    const side = activity.side;
    if (side === 'Left') {
      activity.side = 'Right';
    } else if (side === 'Right') {
      activity.side = 'Left';
    }
    return activity;
  });
}

export function createEmptyActivityInput(
  activityDefinition: ActivityDefinition,
): ActivityInput {
  const id = uuid();
  const { settings } = activityDefinition;

  const activityInput: ActivityInput = {
    id,
    goals: {},
    activityDefinitionId: activityDefinition.id,
  };
  if (settings.reps?.default !== undefined) {
    activityInput.reps = settings.reps.default;
  }
  if (!isEmpty(settings.minimum_to_consider_rep?.options.length)) {
    activityInput.minimumToConsiderRep = Number(settings.minimum_to_consider_rep?.options[0]);
  }
  if (!isEmpty(settings.initial_angle?.options.length)) {
    activityInput.init_angle_degrees = Number(settings.initial_angle?.options[0]);
  }
  if (settings.rest?.default !== undefined) {
    activityInput.rest = settings.rest.default;
  }
  if (settings.hold?.default !== undefined) {
    activityInput.hold = settings.hold.default;
  }
  if (settings.rate?.default !== undefined) {
    activityInput.rate = settings.rate.default;
  }
  if (settings.sets?.default !== undefined) {
    activityInput.sets = settings.sets.default;
  }
  if (settings.distance?.default !== undefined) {
    activityInput.distance = settings.distance.default;
  }
  if (!isEmpty(settings.duration?.default)) {
    activityInput.duration = settings.duration?.default;
  }
  if (settings.type?.options?.includes('Exercise')) {
    activityInput.type = ['Exercise'];
  } else if (settings.type?.options?.includes('Assessment')) {
    activityInput.type = ['Assessment'];
  }
  return activityInput;
}

// The plan being EDITED. Its activities really do occupy slots of this plan, so planIndex and
// slotId are carried across: they are what tie a recorded result back to its exercise, and the
// mapper preserves what it is handed.
export function copyToActivitiesInput(activities: Activity[]) {
  const activitiesInput: ActivityInput[] = [];
  activities.forEach((activity) => {
    const activityDefinition = activity.activityDefinition;
    if (activityDefinition) {
      activitiesInput.push({
        ...createEmptyActivityInput(activity.activityDefinition),
        ...activity,
      });
    }
  });
  return activitiesInput;
}

// A template, a Perci suggestion or an AI-assist plan: exercises arriving as NEW rows of the plan
// being edited.
//
// Their movements carry a planIndex from their OWN numbering — 1..N for a template, 0..N-1 for a
// Perci suggestion — and that number means nothing here. The mapper trusts a planIndex it is
// handed, so carrying one across makes the save claim a slot the plan does not have. On
// production-primary (2026-09-15/16) Perci's zero-based numbering appended to a plan whose real
// slots were 100..400 produced `400 Movement plan index 0 is invalid`; a 1-based template does
// the same thing less obviously, by making the list decrease.
//
// A slotId is dropped for the same reason and one more: it is the mapper's feature-detect for
// slot awareness, so a foreign one would change how the whole body is expressed.
export function copyToNewActivitiesInput(activities: Activity[]): ActivityInput[] {
  return copyToActivitiesInput(activities).map((activityInput) => ({
    ...activityInput,
    planIndex: undefined,
    slotId: undefined,
  }));
}

// The Duplicate button on a row in the edit form.
//
// The copy is a SECOND exercise the patient will perform — not a second reference to the slot the
// original occupies. So it gets a fresh activity id and, like anything else newly added, no slot
// of its own.
//
// Cloning the row wholesale kept both `planIndex` and `slotId`, so the save sent one slot twice:
// `plan_shape_changed: slot ... appears more than once` on a plan with slot ids, and a repeated
// position (`[100, 200, 200]` — which does not increase) on one without.
export function duplicateActivityInput(activity: ActivityInput): ActivityInput {
  return {
    ...cloneDeep(activity),
    id: uuid(),
    planIndex: undefined,
    slotId: undefined,
  };
}

export function copyToTreatmentPlanInput(treatmentPlan: TreatmentPlan): TreatmentPlanInput {
  const today = new Date();
  const startDate = treatmentPlan.startDate;
  const treatmentPlanInput: TreatmentPlanInput = {
    id: uuid(),
    name: treatmentPlan.name,
    description: treatmentPlan.description,
    startDate: new Date(startDate) < today ? toDatePickerFormat(today) : startDate,
    activities: copyToActivitiesInput(treatmentPlan.activities),
  };
  return treatmentPlanInput;
}

export function copyTemplateToTreatmentPlanInput(
  template: TreatmentPlanTemplate,
): TreatmentPlanInput {
  const treatmentPlanInput: TreatmentPlanInput = {
    id: template.id ?? uuid(),
    name: template.name,
    description: template.description,
    startDate: toDatePickerFormat(new Date()),
    activities: copyToNewActivitiesInput(template.activities),
  };
  return treatmentPlanInput;
}

export const DEFAULT_NAME = 'Plan 1';

export function getCategoryOptions(activityDefinition: ActivityDefinition) {
  const categoryOptions: RadioInputFieldOption[] = [];
  const { settings } = activityDefinition;
  if (settings.type?.options.includes('Exercise')) {
    categoryOptions.push({ value: 'Exercise', tooltip: tooltips.exercise });
  }
  if (settings.type?.options.includes('Assessment')) {
    categoryOptions.push({ value: 'Assessment', tooltip: tooltips.assessment });
  }
  return categoryOptions;
}

export function markAccordionItemsAsError(
  formErrors: FormErrors<TreatmentPlanInput> | undefined,
): CSSObject {
  const markAccordionItemAsError = (index: number): CSSObject => {
    const selector: string = `div[data-rbd-draggable-id]:nth-of-type(${index + 1})`;
    return {
      [selector]: {
        'div.MuiAccordion-root': {
          borderColor: 'red',
        },
      },
    };
  };
  let markedItemsCss: CSSObject = {};
  if (!!formErrors?.activities && Array.isArray(formErrors?.activities)) {
    Object.keys(formErrors.activities).forEach((key) => {
      const markedItemCss = markAccordionItemAsError(Number(key));
      markedItemsCss = { ...markedItemsCss, ...markedItemCss };
    });
  }
  return {
    'div[data-rbd-droppable-id]': {
      ...markedItemsCss,
    },
  };
}

export const BODY_PARTS = [
  'Abdominals',
  'Ankles',
  'Back',
  'Balance',
  'Biceps',
  'Calf',
  'Cardio',
  'Cervical',
  'Chest',
  'Core',
  'DNF',
  'Elbows',
  'Foot',
  'Functional',
  'Functional Test',
  'Gait',
  'Hips',
  'Knees',
  'Lower Back',
  'Lumbar',
  'Lumbar Extensors',
  'Neck',
  'Rotator Cuff',
  'Shoulders',
  'Thoracic',
  'Total Body',
  'Triceps',
  'Upper Middle Back',
  'Wrists',
];

// How the View filter panel shows BODY_PARTS. Presentation only: the values are the library's
// own body-part tags, unchanged, so filtering is the same. Every entry of BODY_PARTS must appear
// exactly once across BODY_PART_GROUPS and the tag-based EXERCISE_TYPES
// (planCreationUtils.test.ts checks this), or it would disappear from the panel.
//
// EXERCISE_TYPES is the Exercise Type category. Most are library tags that describe the kind of
// exercise rather than a part of the body; the library stores them in the body-parts field, so
// they are part of the body-part selection and match "any of" together with the body parts.
//
// The library has no field for the others, so they are recognised from the exercise NAME
// (NAME_BASED_EXERCISE_TYPES). That only finds exercises named that way: measured against the
// 375-exercise catalog, 7 "Isometric ..." and 39 "... Stretch". Tagging them in the library's
// body-parts column would make them ordinary tags, and these rules could then go.
export const NAME_BASED_EXERCISE_TYPES = new Map<string, RegExp>([
  ['Isometric', /\bisometrics?\b/i],
  ['Flexibility/Mobility', /\bstretch(es|ing)?\b|\bmobility\b|\bflexibility\b/i],
]);

export const EXERCISE_TYPES = [
  'Isometric',
  'Balance',
  'Cardio',
  'Functional',
  'Functional Test',
  'Flexibility/Mobility',
  'Gait',
];

// Everything that can be ticked in the body-part selection: the library tags plus the
// name-based exercise types. Ticking all of them is the same as "All".
export const BODY_PART_FILTER_OPTIONS = [...BODY_PARTS, ...NAME_BASED_EXERCISE_TYPES.keys()];

// One ticked body part or exercise type: a library tag, or a name rule for the name-based ones.
export function matchesBodyPartFilter(activityDefinition: ActivityDefinition, filter: string) {
  const namePattern = NAME_BASED_EXERCISE_TYPES.get(filter);
  return namePattern
    ? namePattern.test(activityDefinition.name)
    : activityDefinition.bodyParts.includes(filter);
}

export const BODY_PART_GROUPS: { label: string; bodyParts: string[] }[] = [
  {
    label: 'Neck & upper body',
    bodyParts: [
      'Neck',
      'Cervical',
      'DNF',
      'Shoulders',
      'Rotator Cuff',
      'Chest',
      'Biceps',
      'Triceps',
      'Elbows',
      'Wrists',
    ],
  },
  {
    label: 'Back & core',
    bodyParts: [
      'Upper Middle Back',
      'Thoracic',
      'Back',
      'Lower Back',
      'Lumbar',
      'Lumbar Extensors',
      'Abdominals',
      'Core',
    ],
  },
  {
    label: 'Lower body',
    bodyParts: ['Hips', 'Knees', 'Calf', 'Ankles', 'Foot'],
  },
  {
    label: 'Whole body',
    bodyParts: ['Total Body'],
  },
];

// Derived from the one tag table so the dropdown options and the chips on the cards cannot
// drift apart. Matching is by label, never by array index — the tag is no longer a function of
// the phase number alone.
export const MONITORING_TYPES = MONITORING_TAGS.map((tag) => tag.filterLabel);

export const ALL_BODY_PARTS_OPTION = 'All Body Parts';
export const ALL_MONITORING_TYPES_OPTION = 'All Monitoring Types';

// One click in a filter category (Body Part or Monitoring). `allOption` and the specific
// `options` are mutually exclusive: picking a specific option drops "All", picking "All" clears
// the rest, and a selection that ends up empty or covering every option becomes "All". The
// "never empty" rule matters: filterBySelectedFilter shows nothing for an empty category.
export function toggleFilterOption(
  current: string[],
  option: string,
  allOption: string,
  options: string[],
): string[] {
  if (option === allOption) {
    return [allOption];
  }
  const withoutAll = current.filter((value) => value !== allOption);
  const next = withoutAll.includes(option)
    ? withoutAll.filter((value) => value !== option)
    : [...withoutAll, option];
  if (next.length === 0 || options.every((value) => next.includes(value))) {
    return [allOption];
  }
  return next;
}

export const ALL_EQUIPMENT_OPTION = 'All Equipment';

// Every piece of equipment an activity lists: required, optional, and the older plain list.
export function activityEquipmentNames(activityDefinition: ActivityDefinition) {
  return [
    ...(activityDefinition.equipment ?? []),
    ...(activityDefinition.requiredEquipment ?? []).map(({ Name }) => Name),
    ...(activityDefinition.optionalEquipment ?? []).map(({ Name }) => Name),
  ].filter((name) => name?.trim());
}

// The Equipment filter's options: whatever the library's activities use, A to Z. Derived from
// the loaded library, so there is no list to keep in step with it.
export function equipmentFilterOptions(activityDefinitions: ActivityDefinition[]) {
  const names = new Set(activityDefinitions.flatMap(activityEquipmentNames));
  return [...names].sort((a, b) => a.localeCompare(b));
}

// "Any of" the ticked equipment. This is a separate filter from body part and monitoring, so an
// activity must also pass those (filterBySelectedFilter).
export function matchesEquipmentFilter(
  activityDefinition: ActivityDefinition,
  selectedEquipmentFilters: string[],
) {
  if (selectedEquipmentFilters.includes(ALL_EQUIPMENT_OPTION)) {
    return true;
  }
  const names = activityEquipmentNames(activityDefinition);
  return selectedEquipmentFilters.some((equipment) => names.includes(equipment));
}

function matchesMonitoringType(
  activityDefinition: ActivityDefinition,
  monitoringType: string,
) {
  return monitoringTagFor(activityDefinition)?.filterLabel === monitoringType;
}

export function filterBySelectedFilter(
  activityDefinition: ActivityDefinition,
  selectedBodyPartFilters: string[],
  selectedMonitoringTypeFilters: string[],
) {
  if (!selectedBodyPartFilters.length || !selectedMonitoringTypeFilters.length) {
    return false;
  }

  if (
    selectedBodyPartFilters.includes(ALL_BODY_PARTS_OPTION) &&
    selectedMonitoringTypeFilters.includes(ALL_MONITORING_TYPES_OPTION)
  ) {
    return true;
  }

  if (selectedMonitoringTypeFilters.includes(ALL_MONITORING_TYPES_OPTION)) {
    return selectedBodyPartFilters.some((filter) => {
      return matchesBodyPartFilter(activityDefinition, filter);
    });
  }

  if (selectedBodyPartFilters.includes(ALL_BODY_PARTS_OPTION)) {
    return selectedMonitoringTypeFilters.some((monitoringType) => {
      return matchesMonitoringType(activityDefinition, monitoringType);
    });
  }

  return selectedMonitoringTypeFilters.some((monitoringType) => {
    return selectedBodyPartFilters.some((bodyPart) => {
      return (
        matchesBodyPartFilter(activityDefinition, bodyPart) &&
        matchesMonitoringType(activityDefinition, monitoringType)
      );
    });
  });
}

export function useIsPlanHasLocalResults(planId: string | null) {
  const activitiesExecutions = useSelector(getPatientActivitiesExecutionsSelector);

  const isPlanHasLocalResults = activitiesExecutions?.some(
    ({ treatmentPlanId }) => treatmentPlanId === planId,
  );

  return isPlanHasLocalResults;
}

export function createDefultPlanName(): string {
  return createDefaultName('Plan');
}

export function createDefaultTemplateName(): string {
  return createDefaultName('Template');
}
